/**
 * DateTimeInput — планировщик даты+времени в поповере (стиль shadcn "scheduler").
 *
 * Триггер-кнопка показывает читаемое значение ("2 июля 2026, 20:56"), по клику —
 * поповер с календарём (react-day-picker v10, ru-локаль) и двумя dropdown'ами
 * часов/минут (нативные <select> — надёжнее radix Select внутри radix Popover).
 *
 * Контракт значения СОХРАНЁН: value — строка "YYYY-MM-DDTHH:mm" (локальное время,
 * без таймзоны) или "" пока не заданы ОБЕ части (дата И время). Поэтому вызывающий
 * код (TouchCart) как и раньше делает new Date(value)/.toISOString() и валидацию
 * «время в будущем» без изменений. Контрол устойчив к частичному вводу: выбор
 * только даты (без времени) не стирается ре-рендером.
 */

import * as React from "react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface DateTimeInputProps {
  /** Значение в формате datetime-local ("YYYY-MM-DDTHH:mm") или "". */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Нижняя граница (datetime-local), например текущий момент. */
  min?: string;
  /** Шаг времени в секундах (по умолчанию 300 = 5 минут). */
  timeStep?: number;
  className?: string;
  /** id для связи с внешним <Label htmlFor> (вешается на триггер-кнопку). */
  id?: string;
}

function parseValue(v: string): { date: string; hour: string; minute: string } {
  const [d = "", t = ""] = v.split("T");
  const [h = "", m = ""] = t.split(":");
  return { date: d, hour: h.slice(0, 2), minute: m.slice(0, 2) };
}

function build(date: string, hour: string, minute: string): string {
  // Целостное значение только когда заданы ВСЕ части.
  return date && hour && minute ? `${date}T${hour}:${minute}` : "";
}

function two(n: number): string {
  return n.toString().padStart(2, "0");
}

function dateStrToDate(s: string): Date | undefined {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

const selectClass =
  "h-8 rounded-md border border-input bg-transparent px-2 text-sm tabular-nums shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

export function DateTimeInput({
  value,
  onChange,
  disabled,
  min,
  timeStep = 300,
  className,
  id,
}: DateTimeInputProps) {
  const [open, setOpen] = React.useState(false);

  // Локальные половины, засеянные из value. Устойчивость к частичному вводу:
  // до заполнения обеих частей наверх уходит "" (build), но локально не теряем.
  const seed = parseValue(value);
  const [date, setDate] = React.useState(seed.date);
  const [hour, setHour] = React.useState(seed.hour);
  const [minute, setMinute] = React.useState(seed.minute);

  React.useEffect(() => {
    // Синкаемся с родителем только на осмысленные внешние изменения:
    // value непустой (программная установка) ИЛИ у нас уже было полное значение,
    // а родитель его сбросил. Пустой value во время частичного ввода игнорируем.
    if (value !== "" || build(date, hour, minute) !== "") {
      const p = parseValue(value);
      setDate(p.date);
      setHour(p.hour);
      setMinute(p.minute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const minParts = min ? parseValue(min) : { date: "", hour: "", minute: "" };
  const minDate = dateStrToDate(minParts.date);
  const onMinDay = !!minParts.date && date === minParts.date;

  const stepMin = Math.max(1, Math.round(timeStep / 60));
  const hours = React.useMemo(() => Array.from({ length: 24 }, (_, i) => two(i)), []);
  const minutes = React.useMemo(
    () => Array.from({ length: Math.ceil(60 / stepMin) }, (_, i) => two(i * stepMin)),
    [stepMin],
  );

  // На минимальном дне отсекаем прошедшие часы/минуты.
  const availHours = onMinDay ? hours.filter((h) => h >= minParts.hour) : hours;
  const availMinutes =
    onMinDay && hour === minParts.hour ? minutes.filter((m) => m >= minParts.minute) : minutes;

  function emit(d: string, h: string, m: string) {
    setDate(d);
    setHour(h);
    setMinute(m);
    onChange(build(d, h, m));
  }

  function handleDay(picked: Date | undefined) {
    if (!picked) return;
    const ds = `${picked.getFullYear()}-${two(picked.getMonth() + 1)}-${two(picked.getDate())}`;
    let h = hour;
    let m = minute;
    // Если выбран минимальный день — подтянуть время к нижней границе.
    if (minParts.date && ds === minParts.date && h) {
      if (h < minParts.hour) {
        h = minParts.hour;
        m = minParts.minute;
      } else if (h === minParts.hour && m && m < minParts.minute) {
        m = minParts.minute;
      }
    }
    emit(ds, h, m);
  }

  function handleHour(h: string) {
    let m = minute;
    if (onMinDay && h === minParts.hour && m && m < minParts.minute) m = minParts.minute;
    emit(date, h, m);
  }

  const selectedDate = dateStrToDate(date);
  const complete = Boolean(value && date && hour && minute);
  let label = "Выберите дату и время";
  if (complete) {
    try {
      label = format(new Date(`${date}T${hour}:${minute}`), "d MMMM yyyy, HH:mm", { locale: ru });
    } catch {
      label = "Выберите дату и время";
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !complete && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" style={{ color: "var(--accent-hex, #2563eb)" }} />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDay}
          defaultMonth={selectedDate ?? minDate}
          disabled={minDate ? { before: minDate } : undefined}
          autoFocus
        />
        <div className="flex items-center gap-2 border-t p-3">
          <span className="text-sm text-muted-foreground">Время</span>
          <div className="flex items-center gap-1.5">
            <select
              aria-label="Часы"
              value={hour}
              disabled={!date}
              onChange={(e) => handleHour(e.target.value)}
              className={selectClass}
            >
              <option value="" disabled>
                чч
              </option>
              {availHours.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            <span className="text-muted-foreground">:</span>
            <select
              aria-label="Минуты"
              value={minute}
              disabled={!date || !hour}
              onChange={(e) => emit(date, hour, e.target.value)}
              className={selectClass}
            >
              <option value="" disabled>
                мм
              </option>
              {availMinutes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
